import Button from '@aurora/shared-client/components/common/Button/Button';
import { ButtonVariant } from '@aurora/shared-client/components/common/Button/enums';
import EmptyState from '@aurora/shared-client/components/common/EmptyState/EmptyState';
import { IconSize } from '@aurora/shared-client/components/common/Icon/enums';
import { PanelType } from '@aurora/shared-client/components/common/Panel/enums';
import QueryHandler from '@aurora/shared-client/components/common/QueryHandler/QueryHandler';
import AppContext from '@aurora/shared-client/components/context/AppContext/AppContext';
import type {
  UserConstraints,
  UserSorts
} from '@aurora/shared-generated/types/graphql-schema-types';
import { SortDirection } from '@aurora/shared-generated/types/graphql-schema-types';
import { EndUserComponent } from '@aurora/shared-types/pages/enums';
import {
  merge,
  UndefinedValueMergeBehavior
} from '@aurora/shared-utils/helpers/objects/ObjectHelper';
import dynamic from 'next/dynamic';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useClassNameMapper } from 'react-bootstrap';
import { ItemType, UserViewVariant } from '../../../types/enums';
import type {
  GroupHubDetailsQueryVariables,
  NodeMembershipViewNodeModelFragment,
  UserViewFragment,
  UserViewsQuery,
  UserViewsQueryVariables
} from '../../../types/graphql-types';
import PaneledItemList from '../../common/List/PaneledItemList/PaneledItemList';
import EditableWidget from '../../common/Widget/EditableWidget';
import type { WidgetFC } from '../../common/Widget/types';
import type { ItemViewTypeAndProps } from '../../entities/types';
import useGroupHubDetails from '../../grouphubs/useGroupHubDetails';
import useEntityViewQuery from '../../useEntityViewQuery';
import useTranslation from '../../useTranslation';
import type { UserListWidget } from '../types';
import type { UserViewCardProps, UserViewInlineProps } from '../UserView/types';
import userViewsQuery from '../UserViews.query.graphql';

const MemberListForNodeModal = dynamic(
  () => import('../../memberships/MemberListForNodeModal/MemberListForNodeModal')
);

/**
 * Props for UserListForNodeByJoinDateWidget
 */
export interface UserListForNodeByJoinDateWidgetProps extends UserListWidget {
  /**
   * Specifies the sort order for the list.
   */
  sorts?: UserSorts;

  /**
   * Specifies the view of the user for the widget.
   */
  viewVariant?: ItemViewTypeAndProps<ItemType.USER, UserViewVariant>;
}

export const userViewCardInlineDefaults: UserViewInlineProps | UserViewCardProps = {
  useAvatar: true,
  useRole: true,
  showOwnerOnly: true,
  useRank: false,
  useJoinDate: true
};

export const userViewGridDefaults: UserViewInlineProps = {
  useAvatar: true,
  useJoinDate: false,
  useLogin: false,
  useTitle: true,
  avatarSize: IconSize.PX_40
};

const defaultProps: UserListForNodeByJoinDateWidgetProps = {
  viewVariant: {
    type: UserViewVariant.INLINE,
    props: userViewCardInlineDefaults
  },
  useTitle: true,
  hideIfEmpty: true,
  pageSize: 5,
  sorts: {
    joinDate: {
      direction: SortDirection.Desc
    }
  },
  panelType: PanelType.DIVIDER,
  useFooter: true
};

export function getFinalProps(
  props: UserListForNodeByJoinDateWidgetProps
): UserListForNodeByJoinDateWidgetProps {
  return merge(defaultProps, props, {
    undefinedMergeBehavior: UndefinedValueMergeBehavior.IGNORE_BEFORE_MERGE,
    mergeNested: false
  });
}

/**
 * A list of members for the current node, sorted by join date.
 *
 * @constructor
 *
 * @author Jonathan Bridges
 */
const UserListForNodeByJoinDateWidget: WidgetFC<UserListForNodeByJoinDateWidgetProps> = props => {
  const { isVisible, ...rest } = props;
  const finalProps: UserListForNodeByJoinDateWidgetProps = getFinalProps(rest);
  const {
    listVariant,
    viewVariant,
    useTitle,
    hideIfEmpty,
    pageSize,
    sorts,
    className,
    panelType,
    useFooter
  } = finalProps;
  const cx = useClassNameMapper();
  const { contextNode } = useContext(AppContext);
  const { formatMessage, loading: textLoading } = useTranslation(
    EndUserComponent.USER_LIST_FOR_NODE_BY_JOIN_DATE_WIDGET
  );
  const [showModal, setShowModal] = useState(false);

  const handleClose = (): void => setShowModal(false);
  const handleShow = (): void => setShowModal(true);

  const titleToUse = formatMessage('title');

  const useEmpty = !hideIfEmpty;
  const { type: viewVariantType } = viewVariant || {};

  const constraints: UserConstraints = {
    nodeId: {
      eq: contextNode.id
    }
  };

  const queryResult = useEntityViewQuery<ItemType.USER, UserViewsQuery, UserViewsQueryVariables>(
    module,
    ItemType.USER,
    viewVariant,
    userViewsQuery,
    {
      variables: {
        constraints,
        sorts,
        first: pageSize,
        useIsOwner: userViewCardInlineDefaults.showOwnerOnly
      }
    }
  );

  const { data: userData, loading } = queryResult;

  useEffect(() => {
    if (!useEmpty && !loading) {
      isVisible(userData?.users?.edges?.length !== 0);
    }
  }, [userData, isVisible, loading, useEmpty]);

  const variables: GroupHubDetailsQueryVariables = {
    id: contextNode.id,
    useGroupHubPolicies: true,
    useCanAdminNode: true,
    useMembershipInformation: true
  };

  const { queryResult: groupHubQueryResult } = useGroupHubDetails(variables);

  /**
   * A callback function to render Empty State when entity list has not users
   */
  const renderNoUsers = useCallback((): React.ReactElement => {
    return <EmptyState description={formatMessage('emptyDescription')} />;
  }, [formatMessage]);

  if (textLoading) {
    return null;
  }

  return (
    <EditableWidget<UserListForNodeByJoinDateWidgetProps> props={finalProps}>
      <PaneledItemList<
        UserViewFragment,
        ItemType.USER,
        ItemViewTypeAndProps<ItemType.USER, UserViewVariant>,
        UserViewsQuery,
        UserViewsQueryVariables
      >
        type={ItemType.USER}
        variant={viewVariant}
        queryResult={queryResult}
        header={useTitle && titleToUse}
        listVariant={listVariant}
        pageSize={pageSize}
        className={cx(className)}
        panel={panelType}
        useFooter={useFooter}
        footer={(): React.ReactElement => {
          return (
            <QueryHandler queryResult={groupHubQueryResult}>
              {(): React.ReactNode => {
                const { groupHub } = groupHubQueryResult?.data;

                return (
                  groupHub.membersCount > pageSize && (
                    <div
                      className={cx({
                        'd-flex justify-content-center': viewVariantType === UserViewVariant.CARD
                      })}
                    >
                      <Button
                        onClick={handleShow}
                        variant={ButtonVariant.LINK}
                        className={cx('lia-g-loader-btn')}
                      >
                        {formatMessage('showAllButton')}
                      </Button>
                      <MemberListForNodeModal
                        show={showModal}
                        closeCallback={handleClose}
                        node={groupHub as NodeMembershipViewNodeModelFragment}
                        sorts={sorts}
                      />
                    </div>
                  )
                );
              }}
            </QueryHandler>
          );
        }}
        footerClassName={cx('bg-transparent')}
        empty={renderNoUsers}
        useHeaderWhenEmpty={useTitle}
        useEmpty={useEmpty}
      />
    </EditableWidget>
  );
};

export default UserListForNodeByJoinDateWidget;
